import {
  LitElement,
  html,
  css,
  classMap,
  t,
  paths,
  unsafeHTML,
} from '@hh/global.js'

class HHMemberClub extends LitElement {
  static properties = {
    playing: {
      type: Boolean,
      reflect: true,
    },
    currentSlide: {
      type: Number,
    },
    playSpeed: {
      type: Number,
    },
  }

  constructor() {
    super()
    this.playing = true
    this.currentSlide = 0

    this.timerId = null

    this.isMobile = matchMedia('(max-width: 989px)').matches
    if (this.isMobile) {
      this.playing = false
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.startAutoplay()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopAutoplay()
  }

  get allSlides() {
    return this.querySelectorAll('[slot="slide"]')
  }

  onToggleClick() {
    if (this.playing) {
      this.stopAutoplay()
      this.playing = false
    } else {
      this.startAutoplay()
      this.playing = true
    }
  }

  onClickSlide(event) {
    const clickedElement = event.target
    const closestSlide = clickedElement.closest('[slot="slide"]')
    const slideIndex = closestSlide.getAttribute('data-slide-index')
    this.currentSlide = parseInt(slideIndex)

    this.makeActiveSlide()
    this.stopAutoplay()
    if (this.playing) {
      this.startAutoplay()
    }
  }

  startAutoplay() {
    let interval = this.playSpeed
    this.timerId = setInterval(() => {
      this.nextSlide()
    }, interval)
  }

  stopAutoplay() {
    try {
      clearInterval(this.timerId)
      this.timerId = null
    } catch (e) {
      console.log(e)
    }
  }

  nextSlide() {
    if (this.allSlides.length > 1) {
      this.currentSlide = (this.currentSlide + 1) % this.allSlides.length
      this.makeActiveSlide()
    }
  }

  makeActiveSlide() {
    this.allSlides.forEach((slide) => {
      slide.classList.remove('active-item');
    });
    this.allSlides[this.currentSlide].classList.add('active-item')
  }

  onFocusIn() {
    if (!this.isMobile) {
      this.startAnimation()
    }
  }

  onFocusOut() {
    this.stopAnimation()
  }

  render() {
    return html`
      <div
        class="container"
        @focusin=${this.onFocusIn}
        @focusout=${this.onFocusOut}
      >
        <div class="wrapper">
          <div class="heading-mobile">
            <slot name="mobile-content"></slot>
          </div>

          <div class="images">
            <div
              class="images-container"
              style="transform: translateX(${this.currentSlide * -100}%);"
            >
              <slot name="benefitImage"></slot>
            </div>
            <button
              class="toggle"
              @click=${this.onToggleClick}
              aria-label=${this.playing
        ? t('accessibility.pause')
        : t('accessibility.play')}
            >
              ${this.playing ? unsafeHTML(paths.pause) : unsafeHTML(paths.play)}
            </button>
          </div>

          <div class="content">
            <div class="heading-desktop">
              <slot name="content"></slot>
            </div>
            <div class="slides">
              <slot
                name="slide"
                @click=${this.onClickSlide}
                class=${classMap({
          'pause-animation': !this.playing,
        })}
              ></slot>
            </div>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
      display: block;
      background-color: var(--hh-color-background);
    }

    .container {
      height: 100%;
      position: relative;
    }

    .wrapper {
      position: relative;
      height: 100%;
      width: 100%;
    }

    .content {
      padding: 0.9375rem 0 1.5625rem;
    }

    @media (min-width: 990px) {
      .content {
        padding: 2.5000rem 2.5000rem 3.1250rem;
      }
    }
    @media (min-width: 1200px) {
      .content {
        padding: 3.7500rem 4.3750rem 4.3750rem;
      }
    }

    .images {
      position: relative;
      width: 100%;
      height: 100%;
      margin-bottom: 1.2500rem;
      overflow: hidden;
    }

    .images-container {
      display: flex;
      width: 100%;
      height: 100%;
      transition: transform 0.4s ease-in-out;
    }

    .heading-desktop {
      display: none;
    }

    @media (min-width: 990px) {
      .wrapper {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: center;
      }

      .images {
        margin: 0;
      }

      .heading-mobile {
        display: none;
      }

      .heading-desktop {
        display: block;
      }
    }

    @media (min-width: 1500px) {
      .images {
        height: auto;
      }
    }

    .slides {
      display: grid;
      grid-template-columns: 1fr;
      grid-auto-rows: 1fr;
      gap: 10px;
    }

    @media (min-width: 990px) {
      .slides {
        gap: 24px;
      }
    }

    .toggle {
      display: none;
    }

    .toggle svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    @media (min-width: 990px) {
      .toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        position: absolute;
        bottom: 1.2500rem;
        right: 1.2500rem;
        width: 2.6250rem;
        height: 2.6250rem;
        border-radius: 50%;
        border: none;
        color: var(--hh-color-reverse);
        background-color: var(--hh-color-night-medium-opacity-60);
        fill: currentColor;
        cursor: pointer;
      }
    }

    @media (hover: hover) {
      .toggle:is(:focus-visible, :hover) svg {
        fill: currentColor;
      }
    }
  `
}

customElements.define('hh-member-club', HHMemberClub)
